import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** Drop presence if no heartbeat arrives within this window. */
const PRESENCE_TTL_MS = 45000;
const PRESENCE_SWEEP_MS = 10000;

/**
 * Table-phone camera: announce this device as the camera for its tables.
 * `tableNumber` is the main table; `tableNumbers` adds every other table a
 * wide-angle phone films. Coach: listen for announcements to show Record only
 * on tables with a phone.
 */
export function useTableCameraPresence({
  socket = null,
  tsId = null,
  enabled = false,
  tableNumber = null,
  tableNumbers = null,
  userId = null,
  /** When true, listen for presence (coach). */
  listen = false,
} = {}) {
  const [tablesWithCamera, setTablesWithCamera] = useState(() => new Set());
  const [phoneLimit, setPhoneLimit] = useState(false);
  const lastSeenRef = useRef(new Map());

  const coveredKey = useMemo(() => {
    const main = Number(tableNumber);
    const list = (Array.isArray(tableNumbers) ? tableNumbers : [])
      .map(Number)
      .filter((n) => Number.isFinite(n) && n > 0 && n !== main);
    return [...new Set(list)].sort((a, b) => a - b).join(",");
  }, [tableNumber, tableNumbers]);
  const coveredTables = useMemo(() => {
    const main = Number(tableNumber);
    const rest = coveredKey ? coveredKey.split(",").map(Number) : [];
    return Number.isFinite(main) && main > 0 ? [main, ...rest] : rest;
  }, [tableNumber, coveredKey]);

  // Read through a ref so editing coverage re-announces in place instead of
  // flashing every table offline (the server diffs dropped tables itself).
  const coveredTablesRef = useRef(coveredTables);
  coveredTablesRef.current = coveredTables;

  const emitAnnounce = useCallback(
    (online) => {
      const tn = Number(tableNumber);
      if (!socket || !tsId || !userId) return;
      if (!Number.isFinite(tn) || tn <= 0) return;
      socket.emit("announceTableCamera", {
        ts_id: String(tsId),
        table_number: tn,
        table_numbers: coveredTablesRef.current,
        user_id: String(userId),
        online,
      });
    },
    [socket, tsId, tableNumber, userId]
  );

  const announce = useCallback(() => {
    if (!enabled) return;
    emitAnnounce(true);
  }, [enabled, emitAnnounce]);

  const clearAnnounce = useCallback(() => emitAnnounce(false), [emitAnnounce]);

  const firstCoverageRef = useRef(true);
  useEffect(() => {
    if (firstCoverageRef.current) {
      firstCoverageRef.current = false;
      return;
    }
    announce();
  }, [coveredKey, announce]);

  useEffect(() => {
    if (!socket || !listen) return undefined;
    const onPresence = (payload = {}) => {
      if (payload.ts_id != null && tsId != null && String(payload.ts_id) !== String(tsId)) {
        return;
      }
      const tn = Number(payload.table_number);
      if (!Number.isFinite(tn) || tn <= 0) return;
      const online = payload.online !== false && Boolean(payload.user_id);
      const lastSeen = lastSeenRef.current;
      if (online) lastSeen.set(tn, Date.now());
      else lastSeen.delete(tn);
      setTablesWithCamera((prev) => {
        const has = prev.has(tn);
        if (online && has) return prev;
        if (!online && !has) return prev;
        const next = new Set(prev);
        if (online) next.add(tn);
        else next.delete(tn);
        return next;
      });
    };
    socket.on("tableCameraPresence", onPresence);
    const sweep = window.setInterval(() => {
      const now = Date.now();
      const lastSeen = lastSeenRef.current;
      let changed = false;
      const next = new Set();
      for (const [tn, at] of lastSeen.entries()) {
        if (now - at <= PRESENCE_TTL_MS) next.add(tn);
        else {
          lastSeen.delete(tn);
          changed = true;
        }
      }
      if (changed) setTablesWithCamera(next);
    }, PRESENCE_SWEEP_MS);
    return () => {
      socket.off("tableCameraPresence", onPresence);
      window.clearInterval(sweep);
    };
  }, [socket, listen, tsId]);

  useEffect(() => {
    if (!socket || !enabled) return undefined;
    const onRejected = (payload = {}) => {
      if (payload?.code !== "phone_limit") return;
      if (
        payload.ts_id != null &&
        tsId != null &&
        String(payload.ts_id) !== String(tsId)
      ) {
        return;
      }
      setPhoneLimit(true);
    };
    const onPresence = (payload = {}) => {
      if (String(payload.user_id || "") !== String(userId || "")) return;
      if (Number(payload.table_number) !== Number(tableNumber)) return;
      if (payload.online === false) return;
      setPhoneLimit(false);
    };
    socket.on("tableCameraRejected", onRejected);
    socket.on("tableCameraPresence", onPresence);
    return () => {
      socket.off("tableCameraRejected", onRejected);
      socket.off("tableCameraPresence", onPresence);
    };
  }, [socket, enabled, tsId, userId, tableNumber]);

  useEffect(() => {
    const tn = Number(tableNumber);
    if (
      !enabled ||
      !socket ||
      !tsId ||
      !userId ||
      !Number.isFinite(tn) ||
      tn <= 0
    ) {
      return undefined;
    }
    announce();
    const id = window.setInterval(announce, 15000);
    return () => {
      window.clearInterval(id);
      clearAnnounce();
    };
  }, [enabled, socket, tsId, userId, tableNumber, announce, clearAnnounce]);

  const hasCamera = useCallback(
    (tn) => {
      const n = Number(tn);
      if (!Number.isFinite(n) || n <= 0) return false;
      return tablesWithCamera.has(n);
    },
    [tablesWithCamera]
  );

  return {
    tablesWithCamera,
    hasCamera,
    announce,
    phoneLimit,
  };
}

export default useTableCameraPresence;
